import { useCallback, useState } from 'react';
import { MarketSelectorTableHeaderValue } from 'src/components/MarketSelector/MarketSelectorTable/types';
import { MarketSelectorSort } from 'src/components/MarketSelector/types';
import { MarketCategory } from 'src/constants/markets';
import useIsFavoritesSticky from 'src/hooks/tables/useIsFavoritesSticky';

export default function useMarketSelectorState() {
	const [searchValue, setSearchValue] = useState('');
	// Spot trading is disabled, so the selector always opens on the perp markets.
	const [selectedCategory, setSelectedCategory] =
		useState<MarketCategory>('all');
	const [sort, setSort] = useState<MarketSelectorSort>();
	const { isFavoritesSticky, toggleIsFavoritesSticky } = useIsFavoritesSticky();

	/**
	 * Toggles the sort order based on the provided header value.
	 * If the sort order is not currently set or the header value is different from the current sort header,
	 * it sets the sort order to ascending for the "Market" header and descending for other headers.
	 *
	 * The flow of the state is as follows: No sort -> Descending -> Ascending -> No sort.
	 *
	 * For the "Market" header, the flow is: No sort -> Ascending -> Descending -> No sort.
	 */
	const toggleSort = useCallback(
		(header: MarketSelectorTableHeaderValue | undefined) => {
			if (header === undefined) {
				setSort(undefined);
				return;
			}

			if (!sort || sort.header !== header) {
				if (header === 'market') {
					setSort({ header, isDescending: false });
				} else {
					setSort({ header, isDescending: true });
				}
				return;
			}

			if (header === sort.header) {
				if (header === 'market') {
					if (sort.isDescending) {
						setSort(undefined);
					} else {
						setSort({ header, isDescending: true });
					}
					return;
				}

				if (sort.isDescending) {
					setSort({ header, isDescending: false });
				} else {
					setSort(undefined);
				}
				return;
			}
		},
		[sort]
	);

	return {
		searchValue,
		setSearchValue,
		selectedCategory,
		setSelectedCategory,
		sort,
		toggleSort,
		isFavoritesSticky,
		toggleIsFavoritesSticky,
	};
}
